import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { formatDate, cn } from '../../lib/utils';
import { 
  AlertTriangle, FileText, Upload, Plus, Trash2, Edit, Download, 
  User, Home, Phone, Mail, MapPin, Pill, Heart, Shield, Users,
  Clock, Calendar, AlertCircle, ChevronRight, Save, X, Archive, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  MedicationFormDialog,
  AllergyFormDialog,
  EmergencyContactFormDialog,
  ArchiveConfirmDialog,
  EditParticipantProfileDialog,
  MedicalInfoFormDialog,
  MedicalAlertFormDialog,
  GoalFormDialog,
  BehaviourSupportPlanFormDialog
} from '../../components/common/ProfileDialogs';

const ParticipantProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, isAdmin, isHouseManager, user } = useAuth();
  const [participant, setParticipant] = useState(null);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [linkedContactDialogOpen, setLinkedContactDialogOpen] = useState(false);
  const [uploadSection, setUploadSection] = useState('');
  const fileInputRef = useRef(null);

  // New CRUD dialog states
  const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false);
  const [medicationDialogOpen, setMedicationDialogOpen] = useState(false);
  const [allergyDialogOpen, setAllergyDialogOpen] = useState(false);
  const [emergencyContactDialogOpen, setEmergencyContactDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [medicalInfoDialogOpen, setMedicalInfoDialogOpen] = useState(false);
  const [medicalAlertDialogOpen, setMedicalAlertDialogOpen] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [behaviourPlanDialogOpen, setBehaviourPlanDialogOpen] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [selectedAllergy, setSelectedAllergy] = useState(null);
  const [selectedEmergencyContact, setSelectedEmergencyContact] = useState(null);
  const [selectedMedicalInfo, setSelectedMedicalInfo] = useState(null);
  const [selectedMedicalAlert, setSelectedMedicalAlert] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedBehaviourPlan, setSelectedBehaviourPlan] = useState(null);

  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    section: '',
    effective_date: '',
    expiry_date: '',
    review_date: '',
    notes: '',
    tags: ''
  });

  const [newLinkedContact, setNewLinkedContact] = useState({
    name: '',
    relationship: 'family',
    organisation: '',
    phone: '',
    email: '',
    can_view_diary: true,
    can_view_shift_notes: true,
    shift_notes_family_safe_only: true,
    can_view_incidents: true
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [participantRes, housesRes] = await Promise.all([
        api.get(`/participants/${id}`),
        api.get('/houses')
      ]);
      setParticipant(participantRes.data);
      setHouses(housesRes.data);
    } catch (error) {
      toast.error('Failed to load participant');
      navigate('/admin/participants');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section, data) => {
    setSaving(true);
    try {
      await api.put(`/participants/${id}`, { [section]: data });
      toast.success('Saved successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', uploadForm.document_type);
    formData.append('section', uploadForm.section || uploadSection);
    if (uploadForm.effective_date) formData.append('effective_date', uploadForm.effective_date);
    if (uploadForm.expiry_date) formData.append('expiry_date', uploadForm.expiry_date);
    if (uploadForm.review_date) formData.append('review_date', uploadForm.review_date);
    if (uploadForm.notes) formData.append('notes', uploadForm.notes);
    if (uploadForm.tags) formData.append('tags', uploadForm.tags);

    try {
      await api.post(`/participants/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadForm({
        document_type: '',
        section: '',
        effective_date: '',
        expiry_date: '',
        review_date: '',
        notes: '',
        tags: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await api.get(`/participants/${id}/documents/${docId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleAddLinkedContact = async () => {
    try {
      await api.post(`/participants/${id}/linked-contacts`, newLinkedContact);
      toast.success('Contact linked successfully');
      setLinkedContactDialogOpen(false);
      setNewLinkedContact({
        name: '',
        relationship: 'family',
        organisation: '',
        phone: '',
        email: '',
        can_view_diary: true,
        can_view_shift_notes: true,
        shift_notes_family_safe_only: true,
        can_view_incidents: true
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleRemoveLinkedContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to remove this contact?')) return;
    try {
      await api.delete(`/participants/${id}/linked-contacts/${contactId}`);
      toast.success('Contact removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  // ============== PROFILE EDIT HANDLERS ==============
  const handleSaveProfile = async (formData) => {
    setSaving(true);
    try {
      await api.put(`/participants/${id}`, formData);
      toast.success('Profile updated successfully');
      setEditProfileDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // ============== MEDICATION HANDLERS ==============
  const handleSaveMedication = async (formData) => {
    setSaving(true);
    try {
      if (selectedMedication?.id) {
        await api.put(`/participants/${id}/medications/${selectedMedication.id}`, formData);
        toast.success('Medication updated');
      } else {
        await api.post(`/participants/${id}/medications`, formData);
        toast.success('Medication added');
      }
      setMedicationDialogOpen(false);
      setSelectedMedication(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save medication');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMedication = async (medicationId) => {
    if (!window.confirm('Are you sure you want to delete this medication?')) return;
    try {
      await api.delete(`/participants/${id}/medications/${medicationId}`);
      toast.success('Medication deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete medication');
    }
  };

  // ============== ALLERGY HANDLERS ==============
  const handleSaveAllergy = async (formData) => {
    setSaving(true);
    try {
      if (selectedAllergy?.id) {
        await api.put(`/participants/${id}/allergies/${selectedAllergy.id}`, formData);
        toast.success('Allergy updated');
      } else {
        await api.post(`/participants/${id}/allergies`, formData);
        toast.success('Allergy added');
      }
      setAllergyDialogOpen(false);
      setSelectedAllergy(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save allergy');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAllergy = async (allergyId) => {
    if (!window.confirm('Are you sure you want to delete this allergy?')) return;
    try {
      await api.delete(`/participants/${id}/allergies/${allergyId}`);
      toast.success('Allergy deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete allergy');
    }
  };

  // ============== EMERGENCY CONTACT HANDLERS ==============
  const handleSaveEmergencyContact = async (formData) => {
    setSaving(true);
    try {
      if (selectedEmergencyContact?.id) {
        await api.put(`/participants/${id}/emergency-contacts/${selectedEmergencyContact.id}`, formData);
        toast.success('Emergency contact updated');
      } else {
        await api.post(`/participants/${id}/emergency-contacts`, formData);
        toast.success('Emergency contact added');
      }
      setEmergencyContactDialogOpen(false);
      setSelectedEmergencyContact(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save emergency contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEmergencyContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this emergency contact?')) return;
    try {
      await api.delete(`/participants/${id}/emergency-contacts/${contactId}`);
      toast.success('Emergency contact deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete emergency contact');
    }
  };

  // ============== ARCHIVE HANDLERS ==============
  const handleArchive = async (reason) => {
    setSaving(true);
    try {
      await api.post(`/participants/${id}/archive`, { reason });
      toast.success('Participant archived successfully');
      setArchiveDialogOpen(false);
      navigate(-1);
    } catch (error) {
      toast.error('Failed to archive participant');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    setSaving(true);
    try {
      await api.post(`/participants/${id}/restore`);
      toast.success('Participant restored successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to restore participant');
    } finally {
      setSaving(false);
    }
  };

  // ============== MEDICAL INFO HANDLERS ==============
  const handleInlineEdit = async (field, value) => {
    try {
      await api.put('/participants/' + id, { [field]: value });
      setParticipant(prev => ({ ...prev, [field]: value }));
      setEditingField(null);
      setEditValue('');
      toast.success('Updated successfully');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSaveMedicalInfo = async (formData) => {
    setSaving(true);
    try {
      if (selectedMedicalInfo?.id) {
        await api.put(`/participants/${id}/medical-info/${selectedMedicalInfo.id}`, formData);
        toast.success('Medical information updated');
      } else {
        await api.post(`/participants/${id}/medical-info`, formData);
        toast.success('Medical information added');
      }
      setMedicalInfoDialogOpen(false);
      setSelectedMedicalInfo(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save medical information');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMedicalInfo = async (infoId) => {
    if (!window.confirm('Are you sure you want to delete this medical information?')) return;
    try {
      await api.delete(`/participants/${id}/medical-info/${infoId}`);
      toast.success('Medical information deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete medical information');
    }
  };

  // ============== MEDICAL ALERT HANDLERS ==============
  const handleSaveMedicalAlert = async (formData) => {
    setSaving(true);
    try {
      if (selectedMedicalAlert?.id) {
        await api.put(`/participants/${id}/medical-alerts/${selectedMedicalAlert.id}`, formData);
        toast.success('Medical alert updated');
      } else {
        await api.post(`/participants/${id}/medical-alerts`, formData);
        toast.success('Medical alert added');
      }
      setMedicalAlertDialogOpen(false);
      setSelectedMedicalAlert(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save medical alert');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMedicalAlert = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this medical alert?')) return;
    try {
      await api.delete(`/participants/${id}/medical-alerts/${alertId}`);
      toast.success('Medical alert deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete medical alert');
    }
  };

  // ============== GOAL HANDLERS ==============
  const handleSaveGoal = async (formData) => {
    setSaving(true);
    try {
      if (selectedGoal?.id) {
        await api.put(`/participants/${id}/goals/${selectedGoal.id}`, formData);
        toast.success('Goal updated');
      } else {
        await api.post(`/participants/${id}/goals`, formData);
        toast.success('Goal added');
      }
      setGoalDialogOpen(false);
      setSelectedGoal(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      await api.delete(`/participants/${id}/goals/${goalId}`);
      toast.success('Goal deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  // ============== BEHAVIOUR SUPPORT PLAN HANDLERS ==============
  const handleSaveBehaviourPlan = async (formData) => {
    setSaving(true);
    try {
      if (selectedBehaviourPlan?.id) {
        await api.put(`/participants/${id}/behaviour-plans/${selectedBehaviourPlan.id}`, formData);
        toast.success('Behaviour support plan updated');
      } else {
        await api.post(`/participants/${id}/behaviour-plans`, formData);
        toast.success('Behaviour support plan added');
      }
      setBehaviourPlanDialogOpen(false);
      setSelectedBehaviourPlan(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save behaviour support plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBehaviourPlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this behaviour support plan?')) return;
    try {
      await api.delete(`/participants/${id}/behaviour-plans/${planId}`);
      toast.success('Behaviour support plan deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete behaviour support plan');
    }
  };

  const getDocumentsBySection = (section) => {
    return (participant?.documents || []).filter(d => d.section === section && d.is_current);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!participant) return null;

  const canEdit = isAdmin || isHouseManager;
  const canEditAllergies = isAdmin || isHouseManager;
  const canEditMedications = isAdmin;
  const canEditEmergencyContacts = isAdmin;
  const canArchive = isAdmin;
  const isArchived = !!participant.archived_at;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="participant-profile-page">
      {/* Archived Banner */}
      {isArchived && (
        <div className="bg-orange-100 border border-orange-300 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Archive className="h-5 w-5 text-orange-600" />
            <div>
              <p className="font-medium text-orange-800">This participant is archived</p>
              <p className="text-sm text-orange-600">
                Archived on {formatDate(participant.archived_at)}
                {participant.archived_by_name && ` by ${participant.archived_by_name}`}
              </p>
            </div>
          </div>
          {canArchive && (
            <Button onClick={handleRestore} disabled={saving}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Restore
            </Button>
          )}
        </div>
      )}

      <PageHeader 
        title={`${participant.first_name} ${participant.last_name}`}
        description={`NDIS: ${participant.ndis_number} | ${participant.house_name || 'No house assigned'}`}
        actions={
          <div className="flex gap-2">
            {isAdmin && !isArchived && (
              <>
                <Button variant="outline" onClick={() => setEditProfileDialogOpen(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
                <Button variant="outline" className="text-orange-600 hover:bg-orange-50" onClick={() => setArchiveDialogOpen(true)}>
                  <Archive className="h-4 w-4 mr-2" />
                  Archive
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => navigate(-1)}>
              Back to List
            </Button>
          </div>
        }
      />

      {/* Medical Alerts Banner */}
      {participant.medical_alerts?.length > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-2 flex-1">
                <h3 className="font-semibold text-red-600 dark:text-red-400">Medical Alerts</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {participant.medical_alerts.map((alert, idx) => (
                    <div 
                      key={idx} 
                      className={cn(
                        "p-3 rounded-lg border",
                        alert.severity === 'critical' && "bg-red-500/10 border-red-500/30",
                        alert.severity === 'high' && "bg-orange-500/10 border-orange-500/30",
                        alert.severity === 'medium' && "bg-amber-500/10 border-amber-500/30",
                        alert.severity === 'low' && "bg-yellow-500/10 border-yellow-500/30"
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className={cn(
                          "text-xs",
                          alert.severity === 'critical' && "bg-red-500/20 text-red-500",
                          alert.severity === 'high' && "bg-orange-500/20 text-orange-500"
                        )}>
                          {alert.severity?.toUpperCase()}
                        </Badge>
                        <span className="font-medium text-sm">{alert.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{alert.description}</p>
                      {alert.action_required && (
                        <p className="text-xs font-medium text-red-600 dark:text-red-400 mt-1">
                          Action: {alert.action_required}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50 p-1 w-full flex-wrap h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="medical">Medical & Alerts</TabsTrigger>
          <TabsTrigger value="medications">Medications</TabsTrigger>
          <TabsTrigger value="service-plan">Service Plan</TabsTrigger>
          <TabsTrigger value="behaviour">Behaviour Support</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Personal Details */}
            <Card className="border-border/40 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">Full Name</Label>
                    <p className="font-medium">{participant.first_name} {participant.last_name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Date of Birth</Label>
                    <p className="font-medium">{formatDate(participant.date_of_birth)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">NDIS Number</Label>
                    <p className="font-medium">{participant.ndis_number}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Photo Consent</Label>
                    <p className="font-medium">{participant.photo_consent ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Medicare Number</Label>
                    {editingField === 'medicare_number' ? (
                      <div className="flex gap-2">
                        <Input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="e.g. 2123 45670 1"
                          className="h-8"
                        />
                        <Button size="sm" variant="ghost" onClick={() => handleInlineEdit('medicare_number', editValue)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <p className="font-medium cursor-pointer hover:text-primary" onClick={() => { setEditingField('medicare_number'); setEditValue(participant.medicare_number || ''); }}>
                        {participant.medicare_number || <span className="text-muted-foreground italic">Click to add</span>}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Medicare Expiry</Label>
                    {editingField === 'medicare_expiry' ? (
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="h-8"
                        />
                        <Button size="sm" variant="ghost" onClick={() => handleInlineEdit('medicare_expiry', editValue)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <p className="font-medium cursor-pointer hover:text-primary" onClick={() => { setEditingField('medicare_expiry'); setEditValue(participant.medicare_expiry || ''); }}>
                        {participant.medicare_expiry ? formatDate(participant.medicare_expiry) : <span className="text-muted-foreground italic">Click to add</span>}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="border-border/40 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {participant.primary_address ? (
                  <>
                    <div>
                      <Label className="text-muted-foreground text-xs">Primary Address</Label>
                      <p className="font-medium">
                        {participant.primary_address.street}, {participant.primary_address.suburb} {participant.primary_address.state} {participant.primary_address.postcode}
                      </p>
                      {participant.primary_address.access_instructions && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Access: {participant.primary_address.access_instructions}
                        </p>
                      )}
                      {participant.primary_address.is_confidential && (
                        <Badge variant="outline" className="mt-2 text-xs bg-amber-500/10 text-amber-500">
                          Confidential
                        </Badge>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground">No address recorded</p>
                )}
              </CardContent>
            </Card>

            {/* House Link */}
            <Card className="border-border/40 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Home className="h-5 w-5" />
                  House Assignment
                </CardTitle>
              </CardHeader>
              <CardContent>
                {participant.house_id ? (
                  <div className="space-y-2">
                    <p className="font-medium text-lg">{participant.house_name}</p>
                    {houses.find(h => h.id === participant.house_id)?.staffing_ratio && (
                      <p className="text-sm text-muted-foreground">
                        Staffing Ratio: {houses.find(h => h.id === participant.house_id)?.staffing_ratio}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground mb-3">No house assigned (Community Client)</p>
                    {canEdit && (
                      <Select onValueChange={(v) => handleSave('house_id', v)}>
                        <SelectTrigger className="w-48 mx-auto">
                          <SelectValue placeholder="Assign to house" />
                        </SelectTrigger>
                        <SelectContent>
                          {houses.map(h => (
                            <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* NDIS Plan */}
            <Card className="border-border/40 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  NDIS Plan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">Plan Start</Label>
                    <p className="font-medium">{formatDate(participant.plan_start_date) || 'Not set'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Plan End</Label>
                    <p className="font-medium">{formatDate(participant.plan_end_date) || 'Not set'}</p>
                  </div>
                </div>
                {participant.support_categories?.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground text-xs">Support Categories</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {participant.support_categories.map((cat, idx) => (
                        <Badge key={idx} variant="secondary">{cat}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Medical & Alerts Tab */}
        <TabsContent value="medical" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Allergies */}
            <Card className="border-border/40 bg-card/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  Allergies
                </CardTitle>
                {canEditAllergies && !isArchived && (
                  <Button size="sm" onClick={() => { setSelectedAllergy(null); setAllergyDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Allergy
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {participant.allergies?.length > 0 ? (
                  <div className="space-y-3">
                    {participant.allergies.map((allergy, idx) => (
                      <div key={allergy.id || idx} className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{allergy.allergy_name || allergy.type}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              (allergy.severity === 'Severe' || allergy.severity === 'severe') && "bg-red-500/20 text-red-500",
                              (allergy.severity === 'Moderate' || allergy.severity === 'moderate') && "bg-orange-500/20 text-orange-500",
                              (allergy.severity === 'Mild' || allergy.severity === 'mild') && "bg-yellow-500/20 text-yellow-500"
                            )}>
                              {allergy.severity}
                            </Badge>
                            {canEditAllergies && !isArchived && (
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setSelectedAllergy(allergy); setAllergyDialogOpen(true); }}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDeleteAllergy(allergy.id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">Reaction: {allergy.reaction}</p>
                        {(allergy.notes || allergy.action_plan) && (
                          <p className="text-sm font-medium text-red-600 mt-1">Notes: {allergy.notes || allergy.action_plan}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No allergies recorded</p>
                )}
              </CardContent>
            </Card>

            {/* Medical Alerts */}
            <Card className="border-border/40 bg-card/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Medical Alerts
                </CardTitle>
                {canEdit && !isArchived && (
                  <Button size="sm" onClick={() => { setSelectedMedicalAlert(null); setMedicalAlertDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Alert
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {participant.medical_alerts?.length > 0 ? (
                  <div className="space-y-3">
                    {participant.medical_alerts.map((alert, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-muted/30 border border-border/40">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs capitalize">{alert.alert_type}</Badge>
                          <span className="font-medium">{alert.title}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{alert.description}</p>
                        {alert.action_required && (
                          <p className="text-sm font-medium text-primary mt-2">
                            <strong>Action Required:</strong> {alert.action_required}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No medical alerts</p>
                )}
              </CardContent>
            </Card>

            {/* Medical Info */}
            <Card className="border-border/40 bg-card/50 lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Heart className="h-5 w-5" />
                  Medical Conditions
                </CardTitle>
                {canEdit && !isArchived && (
                  <Button size="sm" onClick={() => { setSelectedMedicalInfo(null); setMedicalInfoDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Condition
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {participant.medical_conditions?.length > 0 ? (
                  <div className="space-y-3 mb-4">
                    {participant.medical_conditions.map((info, idx) => (
                      <div key={info.id || idx} className="p-3 rounded-lg bg-muted/30 border border-border/40">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{info.condition}</span>
                          {canEdit && !isArchived && info.id && (
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setSelectedMedicalInfo(info); setMedicalInfoDialogOpen(true); }}>
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDeleteMedicalInfo(info.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        {info.treating_doctor && <p className="text-sm text-muted-foreground">Doctor: {info.treating_doctor}</p>}
                        {info.treatment_plan && <p className="text-sm mt-1">{info.treatment_plan}</p>}
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <Label className="text-muted-foreground text-xs">GP / Doctor</Label>
                    <p className="font-medium">{participant.medical_info?.gp || 'Not recorded'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Emergency Protocol</Label>
                    <p className="text-sm">{participant.medical_info?.emergency_protocol || 'Not recorded'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Medications Tab */}
        <TabsContent value="medications" className="space-y-6">
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Pill className="h-5 w-5" />
                Medications
              </CardTitle>
              {canEditMedications && !isArchived && (
                <Button size="sm" onClick={() => { setSelectedMedication(null); setMedicationDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Medication
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {participant.medications?.length > 0 ? (
                <div className="space-y-4">
                  {participant.medications.filter(m => m.is_active !== false).map((med, idx) => (
                    <div key={med.id || idx} className={cn(
                      "p-4 rounded-lg border",
                      med.is_high_risk ? "bg-red-500/5 border-red-500/30" : "bg-muted/30 border-border/40"
                    )}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{med.medication_name || med.name}</h4>
                            {med.is_high_risk && (
                              <Badge className="bg-red-500 text-white text-xs">HIGH RISK</Badge>
                            )}
                            {med.is_prn && (
                              <Badge variant="outline" className="text-xs">PRN</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {med.dose} - {med.frequency} ({med.route})
                          </p>
                        </div>
                        {canEditMedications && !isArchived && (
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setSelectedMedication(med); setMedicationDialogOpen(true); }}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => handleDeleteMedication(med.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3 text-sm">
                        <div>
                          <Label className="text-muted-foreground text-xs">Prescriber</Label>
                          <p>{med.prescribing_doctor || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Start Date</Label>
                          <p>{formatDate(med.start_date) || 'N/A'}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-muted-foreground text-xs">Instructions</Label>
                          <p>{med.instructions || 'N/A'}</p>
                        </div>
                      </div>
                      {med.prn_rules && (
                        <div className="mt-3 p-2 rounded bg-amber-500/10 border border-amber-500/30">
                          <Label className="text-muted-foreground text-xs">PRN Rules</Label>
                          <p className="text-sm">{med.prn_rules}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No medications recorded</p>
              )}
            </CardContent>
          </Card>

          {/* Medication Documents */}
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Medication Charts & Documents</CardTitle>
              {canEdit && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setUploadSection('medical');
                    setUploadForm({ ...uploadForm, section: 'medical', document_type: 'medication_chart' });
                    setUploadDialogOpen(true);
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <DocumentList 
                documents={getDocumentsBySection('medical')}
                onDownload={handleDownloadDocument}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Plan Tab */}
        <TabsContent value="service-plan" className="space-y-6">
          <Card className="border-border/40 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg">Service Plan</CardTitle>
              {participant.service_plan?.review_date && (
                <CardDescription>
                  Review Date: {formatDate(participant.service_plan.review_date)}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Goals */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Goals</Label>
                  {canEdit && !isArchived && (
                    <Button size="sm" variant="outline" onClick={() => { setSelectedGoal(null); setGoalDialogOpen(true); }}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Goal
                    </Button>
                  )}
                </div>
                {participant.service_plan?.goals?.length > 0 || participant.goals?.length > 0 ? (
                  <div className="space-y-3">
                    {(participant.goals || participant.service_plan?.goals || []).map((goal, idx) => (
                      <div key={goal.id || idx} className="p-3 rounded-lg bg-muted/30 border border-border/40">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{goal.goal_title || goal.goal}</p>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              (goal.status === 'active' || goal.status === 'In Progress') && "bg-green-500/10 text-green-500",
                              (goal.status === 'achieved' || goal.status === 'Achieved') && "bg-blue-500/10 text-blue-500",
                              (goal.status === 'on_hold' || goal.status === 'On Hold') && "bg-amber-500/10 text-amber-500"
                            )}>
                              {goal.status}
                            </Badge>
                            {canEdit && !isArchived && goal.id && (
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setSelectedGoal(goal); setGoalDialogOpen(true); }}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDeleteGoal(goal.id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        {(goal.supports || goal.description) && (
                          <p className="text-sm text-muted-foreground mt-1">{goal.description || `Supports: ${goal.supports}`}</p>
                        )}
                        {goal.progress_notes && (
                          <p className="text-sm mt-2">{goal.progress_notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground mt-2">No goals recorded</p>
                )}
              </div>

              {/* Routines */}
              {participant.service_plan?.routines && (
                <div>
                  <Label className="text-base font-semibold">Daily Routines</Label>
                  <p className="mt-2 p-3 rounded-lg bg-muted/30">{participant.service_plan.routines}</p>
                </div>
              )}

              {/* Staffing & Community Access */}
              <div className="grid gap-4 sm:grid-cols-2">
                {participant.service_plan?.staffing_ratio_needs && (
                  <div>
                    <Label className="text-base font-semibold">Staffing Ratio Needs</Label>
                    <p className="mt-2">{participant.service_plan.staffing_ratio_needs}</p>
                  </div>
                )}
                {participant.service_plan?.community_access_preferences && (
                  <div>
                    <Label className="text-base font-semibold">Community Access Preferences</Label>
                    <p className="mt-2">{participant.service_plan.community_access_preferences}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Service Plan Documents */}
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Service Plan Documents</CardTitle>
              {canEdit && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setUploadSection('service_plan');
                    setUploadForm({ ...uploadForm, section: 'service_plan', document_type: 'service_plan' });
                    setUploadDialogOpen(true);
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <DocumentList 
                documents={getDocumentsBySection('service_plan')}
                onDownload={handleDownloadDocument}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Behaviour Support Tab */}
        <TabsContent value="behaviour" className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Behaviour Support Plans</h3>
            {canEdit && !isArchived && (
              <Button onClick={() => { setSelectedBehaviourPlan(null); setBehaviourPlanDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Behaviour Plan
              </Button>
            )}
          </div>
          {participant.behaviour_support_plan || participant.behaviour_plans?.length > 0 ? (
            <>
              <Card className="border-border/40 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg">Behaviour Support Plan</CardTitle>
                  {participant.behaviour_support_plan.review_date && (
                    <CardDescription>
                      Review Date: {formatDate(participant.behaviour_support_plan.review_date)}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-6">
                  {participant.behaviour_support_plan.strategies && (
                    <>
                      {/* Triggers */}
                      {participant.behaviour_support_plan.strategies.triggers?.length > 0 && (
                        <div>
                          <Label className="text-base font-semibold text-red-500">Triggers</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {participant.behaviour_support_plan.strategies.triggers.map((t, idx) => (
                              <Badge key={idx} variant="outline" className="bg-red-500/10 text-red-500">
                                {t}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Early Warning Signs */}
                      {participant.behaviour_support_plan.strategies.early_warning_signs?.length > 0 && (
                        <div>
                          <Label className="text-base font-semibold text-amber-500">Early Warning Signs</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {participant.behaviour_support_plan.strategies.early_warning_signs.map((s, idx) => (
                              <Badge key={idx} variant="outline" className="bg-amber-500/10 text-amber-500">
                                {s}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Proactive Strategies */}
                      {participant.behaviour_support_plan.strategies.proactive_strategies?.length > 0 && (
                        <div>
                          <Label className="text-base font-semibold text-green-500">Proactive Strategies</Label>
                          <ul className="mt-2 space-y-1">
                            {participant.behaviour_support_plan.strategies.proactive_strategies.map((s, idx) => (
                              <li key={idx} className="text-sm flex items-start gap-2">
                                <ChevronRight className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* De-escalation Steps */}
                      {participant.behaviour_support_plan.strategies.de_escalation_steps?.length > 0 && (
                        <div>
                          <Label className="text-base font-semibold text-blue-500">De-escalation Steps</Label>
                          <ol className="mt-2 space-y-1 list-decimal list-inside">
                            {participant.behaviour_support_plan.strategies.de_escalation_steps.map((s, idx) => (
                              <li key={idx} className="text-sm">{s}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </>
                  )}

                  {/* Restrictive Practices */}
                  {(participant.behaviour_support_plan.restrictive_practice_authorised || 
                    participant.behaviour_support_plan.restrictive_practice_indicators) && (
                    <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                      <Label className="text-base font-semibold text-purple-500 flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Restrictive Practices
                      </Label>
                      {participant.behaviour_support_plan.restrictive_practice_authorised && (
                        <Badge className="mt-2 bg-purple-500">Authorised</Badge>
                      )}
                      {participant.behaviour_support_plan.restrictive_practice_indicators && (
                        <p className="mt-2 text-sm">{participant.behaviour_support_plan.restrictive_practice_indicators}</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* BSP Specialist */}
              {participant.behaviour_support_specialist?.name && (
                <Card className="border-border/40 bg-card/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Behaviour Support Specialist</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label className="text-muted-foreground text-xs">Name</Label>
                        <p className="font-medium">{participant.behaviour_support_specialist.name}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Organisation</Label>
                        <p>{participant.behaviour_support_specialist.organisation || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Phone</Label>
                        <p>{participant.behaviour_support_specialist.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Email</Label>
                        <p>{participant.behaviour_support_specialist.email || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Role</Label>
                        <p>{participant.behaviour_support_specialist.role || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Preferred Contact</Label>
                        <p>{participant.behaviour_support_specialist.preferred_contact_method || 'N/A'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-border/40 bg-card/50">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No Behaviour Support Plan recorded</p>
              </CardContent>
            </Card>
          )}

          {/* BSP Documents */}
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">BSP Documents</CardTitle>
              {canEdit && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setUploadSection('bsp');
                    setUploadForm({ ...uploadForm, section: 'bsp', document_type: 'behaviour_support_plan' });
                    setUploadDialogOpen(true);
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <DocumentList 
                documents={getDocumentsBySection('bsp')}
                onDownload={handleDownloadDocument}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-6">
          {/* Emergency Contacts */}
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="h-5 w-5 text-red-500" />
                Emergency Contacts
              </CardTitle>
              {canEditEmergencyContacts && !isArchived && (
                <Button size="sm" onClick={() => { setSelectedEmergencyContact(null); setEmergencyContactDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Contact
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {participant.emergency_contacts?.length > 0 ? (
                <div className="space-y-3">
                  {participant.emergency_contacts.sort((a, b) => (a.priority || 1) - (b.priority || 1)).map((contact, idx) => (
                    <div key={contact.id || idx} className="p-4 rounded-lg bg-muted/30 border border-border/40">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={cn("text-xs", contact.is_primary && "bg-green-100 text-green-700")}>
                              {contact.is_primary ? 'Primary' : contact.priority === 1 ? 'Primary' : `Priority ${contact.priority || idx + 1}`}
                            </Badge>
                            <span className="font-medium">{contact.full_name || contact.name}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{contact.relationship}</p>
                        </div>
                        {canEditEmergencyContacts && !isArchived && (
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setSelectedEmergencyContact(contact); setEmergencyContactDialogOpen(true); }}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => handleDeleteEmergencyContact(contact.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                        <div>
                          <Label className="text-muted-foreground text-xs">Phone</Label>
                          <p>{contact.phone || contact.phone_primary}</p>
                          {contact.phone_secondary && (
                            <p className="text-muted-foreground">{contact.phone_secondary}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Email</Label>
                          <p>{contact.email || 'N/A'}</p>
                        </div>
                      </div>
                      {contact.address && (
                        <div className="mt-2 text-sm">
                          <Label className="text-muted-foreground text-xs">Address</Label>
                          <p>{contact.address}</p>
                        </div>
                      )}
                      {(contact.notes || contact.preferred_contact_hours) && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {contact.notes || `Best time to contact: ${contact.preferred_contact_hours}`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No emergency contacts recorded</p>
              )}
            </CardContent>
          </Card>

          {/* Guardian */}
          {participant.guardian?.is_applicable !== false && participant.guardian?.name && (
            <Card className="border-border/40 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Guardian / Decision Maker
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-muted-foreground text-xs">Name</Label>
                    <p className="font-medium">{participant.guardian.name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Relationship</Label>
                    <p>{participant.guardian.relationship}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Authority Type</Label>
                    <p>{participant.guardian.authority_type || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Scope</Label>
                    <p>{participant.guardian.scope || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Linked Contacts (Family/SC) */}
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Linked Contacts (Family / Support Coordinators)
              </CardTitle>
              {canEdit && (
                <Button size="sm" onClick={() => setLinkedContactDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {participant.linked_contacts?.length > 0 ? (
                <div className="space-y-3">
                  {participant.linked_contacts.map((contact, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-muted/30 border border-border/40">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{contact.name}</span>
                            <Badge variant="outline" className="text-xs capitalize">{contact.relationship}</Badge>
                          </div>
                          {contact.organisation && (
                            <p className="text-sm text-muted-foreground">{contact.organisation}</p>
                          )}
                        </div>
                        {canEdit && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleRemoveLinkedContact(contact.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                        <div>
                          <Label className="text-muted-foreground text-xs">Phone</Label>
                          <p>{contact.phone || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Email</Label>
                          <p>{contact.email || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Badge variant={contact.can_view_diary ? "default" : "secondary"} className="text-xs">
                          {contact.can_view_diary ? '✓' : '✗'} Daily Diary
                        </Badge>
                        <Badge variant={contact.can_view_shift_notes ? "default" : "secondary"} className="text-xs">
                          {contact.can_view_shift_notes ? '✓' : '✗'} Shift Notes
                          {contact.shift_notes_family_safe_only && ' (Safe Only)'}
                        </Badge>
                        <Badge variant={contact.can_view_incidents ? "default" : "secondary"} className="text-xs">
                          {contact.can_view_incidents ? '✓' : '✗'} Incidents
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No linked contacts</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6">
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">NDIS Forms & Documents</CardTitle>
              {canEdit && (
                <Button 
                  onClick={() => {
                    setUploadSection('ndis_forms');
                    setUploadForm({ ...uploadForm, section: 'ndis_forms' });
                    setUploadDialogOpen(true);
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <DocumentList 
                documents={getDocumentsBySection('ndis_forms')}
                onDownload={handleDownloadDocument}
              />
            </CardContent>
          </Card>

          {/* All Documents by Section */}
          <div className="grid gap-6 lg:grid-cols-2">
            {['guardian', 'emergency'].map(section => (
              <Card key={section} className="border-border/40 bg-card/50">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg capitalize">{section.replace('_', ' ')} Documents</CardTitle>
                  {canEdit && (
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setUploadSection(section);
                        setUploadForm({ ...uploadForm, section });
                        setUploadDialogOpen(true);
                      }}
                    >
                      <Upload className="h-4 w-4" />
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <DocumentList 
                    documents={getDocumentsBySection(section)}
                    onDownload={handleDownloadDocument}
                    compact
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Type *</Label>
              <Select 
                value={uploadForm.document_type} 
                onValueChange={(v) => setUploadForm({...uploadForm, document_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="service_agreement">Service Agreement</SelectItem>
                  <SelectItem value="consent_form">Consent Form</SelectItem>
                  <SelectItem value="plan_document">Plan Document</SelectItem>
                  <SelectItem value="assessment">Assessment</SelectItem>
                  <SelectItem value="incident_correspondence">Incident Correspondence</SelectItem>
                  <SelectItem value="restrictive_practice_auth">Restrictive Practice Authorisation</SelectItem>
                  <SelectItem value="medication_chart">Medication Chart</SelectItem>
                  <SelectItem value="behaviour_support_plan">Behaviour Support Plan</SelectItem>
                  <SelectItem value="service_plan">Service Plan</SelectItem>
                  <SelectItem value="guardianship">Guardianship Order</SelectItem>
                  <SelectItem value="emergency_plan">Emergency Plan</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input 
                  type="date"
                  value={uploadForm.effective_date}
                  onChange={(e) => setUploadForm({...uploadForm, effective_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input 
                  type="date"
                  value={uploadForm.expiry_date}
                  onChange={(e) => setUploadForm({...uploadForm, expiry_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Review Date</Label>
              <Input 
                type="date"
                value={uploadForm.review_date}
                onChange={(e) => setUploadForm({...uploadForm, review_date: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={uploadForm.notes}
                onChange={(e) => setUploadForm({...uploadForm, notes: e.target.value})}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input 
                value={uploadForm.tags}
                onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})}
                placeholder="e.g., medical, urgent, review"
              />
            </div>

            <div className="space-y-2">
              <Label>File *</Label>
              <Input 
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                ref={fileInputRef}
              />
              <p className="text-xs text-muted-foreground">Supported: PDF, DOCX, JPG, PNG</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => handleUploadDocument(fileInputRef.current?.files?.[0])}>
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Linked Contact Dialog */}
      <Dialog open={linkedContactDialogOpen} onOpenChange={setLinkedContactDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Linked Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input 
                value={newLinkedContact.name}
                onChange={(e) => setNewLinkedContact({...newLinkedContact, name: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Relationship *</Label>
              <Select 
                value={newLinkedContact.relationship} 
                onValueChange={(v) => setNewLinkedContact({...newLinkedContact, relationship: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="family">Family Member</SelectItem>
                  <SelectItem value="support_coordinator">Support Coordinator</SelectItem>
                  <SelectItem value="representative">Representative</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Organisation</Label>
              <Input 
                value={newLinkedContact.organisation}
                onChange={(e) => setNewLinkedContact({...newLinkedContact, organisation: e.target.value})}
                placeholder="For Support Coordinators"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={newLinkedContact.phone}
                  onChange={(e) => setNewLinkedContact({...newLinkedContact, phone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={newLinkedContact.email}
                  onChange={(e) => setNewLinkedContact({...newLinkedContact, email: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-border/40">
              <Label className="text-base font-semibold">Permissions</Label>
              
              <div className="flex items-center justify-between">
                <Label>Can view Daily Diary</Label>
                <Switch 
                  checked={newLinkedContact.can_view_diary}
                  onCheckedChange={(c) => setNewLinkedContact({...newLinkedContact, can_view_diary: c})}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Can view Shift Notes</Label>
                <Switch 
                  checked={newLinkedContact.can_view_shift_notes}
                  onCheckedChange={(c) => setNewLinkedContact({...newLinkedContact, can_view_shift_notes: c})}
                />
              </div>

              {newLinkedContact.can_view_shift_notes && (
                <div className="flex items-center justify-between pl-4">
                  <Label className="text-sm">Family-safe notes only</Label>
                  <Switch 
                    checked={newLinkedContact.shift_notes_family_safe_only}
                    onCheckedChange={(c) => setNewLinkedContact({...newLinkedContact, shift_notes_family_safe_only: c})}
                  />
                </div>
              )}

              <div className="flex items-center justify-between">
                <Label>Can view Incident Reports (after admin sign-off)</Label>
                <Switch 
                  checked={newLinkedContact.can_view_incidents}
                  onCheckedChange={(c) => setNewLinkedContact({...newLinkedContact, can_view_incidents: c})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkedContactDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddLinkedContact}>Add Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <EditParticipantProfileDialog
        open={editProfileDialogOpen}
        onOpenChange={setEditProfileDialogOpen}
        participant={participant}
        houses={houses}
        onSave={handleSaveProfile}
        loading={saving}
      />

      {/* Medication Dialog */}
      <MedicationFormDialog
        open={medicationDialogOpen}
        onOpenChange={setMedicationDialogOpen}
        medication={selectedMedication}
        onSave={handleSaveMedication}
        loading={saving}
      />

      {/* Allergy Dialog */}
      <AllergyFormDialog
        open={allergyDialogOpen}
        onOpenChange={setAllergyDialogOpen}
        allergy={selectedAllergy}
        onSave={handleSaveAllergy}
        loading={saving}
      />

      {/* Emergency Contact Dialog */}
      <EmergencyContactFormDialog
        open={emergencyContactDialogOpen}
        onOpenChange={setEmergencyContactDialogOpen}
        contact={selectedEmergencyContact}
        onSave={handleSaveEmergencyContact}
        loading={saving}
      />

      {/* Archive Confirmation Dialog */}
      <ArchiveConfirmDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        itemType="Participant"
        itemName={`${participant?.first_name} ${participant?.last_name}`}
        onConfirm={handleArchive}
        loading={saving}
      />

      {/* Medical Info Dialog */}
      <MedicalInfoFormDialog
        open={medicalInfoDialogOpen}
        onOpenChange={setMedicalInfoDialogOpen}
        medicalInfo={selectedMedicalInfo}
        onSave={handleSaveMedicalInfo}
        loading={saving}
      />

      {/* Medical Alert Dialog */}
      <MedicalAlertFormDialog
        open={medicalAlertDialogOpen}
        onOpenChange={setMedicalAlertDialogOpen}
        alert={selectedMedicalAlert}
        onSave={handleSaveMedicalAlert}
        loading={saving}
      />

      {/* Goal Dialog */}
      <GoalFormDialog
        open={goalDialogOpen}
        onOpenChange={setGoalDialogOpen}
        goal={selectedGoal}
        onSave={handleSaveGoal}
        loading={saving}
      />

      {/* Behaviour Support Plan Dialog */}
      <BehaviourSupportPlanFormDialog
        open={behaviourPlanDialogOpen}
        onOpenChange={setBehaviourPlanDialogOpen}
        plan={selectedBehaviourPlan}
        onSave={handleSaveBehaviourPlan}
        loading={saving}
      />
    </div>
  );
};

// Document List Component
const DocumentList = ({ documents, onDownload, compact = false }) => {
  if (!documents || documents.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-4">
        No documents uploaded
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {documents.map((doc) => (
        <div 
          key={doc.id} 
          className={cn(
            "flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/40",
            compact && "p-2"
          )}
        >
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className={cn("font-medium", compact && "text-sm")}>{doc.filename}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{doc.document_type?.replace(/_/g, ' ')}</span>
                <span>•</span>
                <span>{formatDate(doc.upload_date)}</span>
                {doc.version > 1 && <Badge variant="outline" className="text-xs">v{doc.version}</Badge>}
              </div>
              {doc.expiry_date && (
                <p className={cn(
                  "text-xs mt-1",
                  new Date(doc.expiry_date) < new Date() && "text-red-500",
                  new Date(doc.expiry_date) < new Date(Date.now() + 30*24*60*60*1000) && "text-amber-500"
                )}>
                  Expires: {formatDate(doc.expiry_date)}
                </p>
              )}
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => onDownload(doc.id, doc.filename)}
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
};

// Main component needs dialogs - find where to add them
// Add dialogs to the main return statement

export default ParticipantProfilePage;
